import React from 'react';
import { Route, Switch, useLocation, Redirect } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { InitAuth } from './services/authService';
import { useAuthStore } from './store/useAuthStore';
import { useState, useEffect } from 'react';
import { logInfo } from './services/loggerService';

import { useIsMobile, useIsXL } from './hooks/useMediaQuery';
import { DesktopSidebar, DesktopHeader, MobileHeader, MobileSidebar } from './components/ui';
import { RefreshProvider } from './contexts/RefreshContext';
import { ToastProvider } from './contexts/ToastContext';
import { SignInPage } from './pages/SignInPage';
import { GlobalErrorModal } from './components/ui/GlobalErrorModal';
import { AuthGuard } from './components/auth/AuthGuard';
import Dashboard from './pages/Dashboard';
import ClientsPage from './pages/ClientsPage';
import PropertiesPage from './pages/PropertiesPage';
import AgentsPage from './pages/AgentsPage';
import TeamsPage from './pages/TeamsPage';
import ProfilesPage from './pages/ProfilesPage';
import CallsPage from './pages/CallsPage';
import CallReportPage from './pages/CallReportPage';
import AIAgentsPage from './pages/AIAgentsPage';
import PhoneNumbersPage from './pages/PhoneNumbersPage';
import VoicesPage from './pages/VoicesPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientAuditPage from './pages/ClientAuditPage';
import UsersPage from './pages/UsersPage';
import ContentPage from './pages/content/ContentPage';


/* Global styles (includes theme variables) */
import './styles/global.scss';

setupIonicReact();

// Waits for auth loading to complete before deciding to show content or redirect
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) return null;
  if (!isAuthenticated) return <Redirect to="/sign-in" />;
  return <AuthGuard>{children}</AuthGuard>;
}

function AppLayout() {
  const location = useLocation();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const isMobile = useIsMobile();
  const isXL = useIsXL();
  const { isAuthenticated, isLoading } = useAuthStore();

  // Automatically manage sidebar state based on screen size
  useEffect(() => {
    if (isXL) {
      setIsDesktopSidebarOpen(true); // Always open on XL screens
    } else {
      setIsDesktopSidebarOpen(false); // Default closed on smaller screens
    }
  }, [isXL]);

  // Root route: redirect to sign-in or dashboard
  if (location.pathname === '/') {
    if (isLoading) return null;
    return (
      <IonRouterOutlet id="main-content" animated={isMobile}>
        <Route exact path="/">
          <Redirect to={isAuthenticated ? '/dashboard' : '/sign-in'} />
        </Route>
      </IonRouterOutlet>
    );
  }

  return (
    <>
      {/* Mobile Side Menu (Drawer) */}
      <MobileSidebar disabled={!isMobile} />

      {/* Desktop Sidebar (Fixed) */}
      {!isMobile && isAuthenticated && (
        <DesktopSidebar isOpen={isDesktopSidebarOpen} />
      )}

      {/* Main Layout Container */}
      <div className={`min-h-screen flex flex-col transition-all duration-300 ease-in-out ${isAuthenticated && !isMobile && isDesktopSidebarOpen ? 'ml-64' : isAuthenticated && !isMobile ? 'ml-20' : ''}`}>
        {/* Header */}
        {isAuthenticated && (
          isMobile ? (
            <MobileHeader />
          ) : (
            <DesktopHeader onToggleSidebar={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)} isXL={isXL} />
          )
        )}

        {/* Page Content */}
        <div className="flex-1 relative">
          <Switch>
            <Route exact path="/dashboard">
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            </Route>
            <Route exact path="/clients">
              <ProtectedRoute><ClientsPage /></ProtectedRoute>
            </Route>
            <Route exact path="/client-detail">
              <ProtectedRoute><ClientDetailPage /></ProtectedRoute>
            </Route>
            <Route exact path="/client-audit">
              <ProtectedRoute><ClientAuditPage /></ProtectedRoute>
            </Route>
            <Route exact path="/users">
              <ProtectedRoute><UsersPage /></ProtectedRoute>
            </Route>
            <Route exact path="/properties">
              <ProtectedRoute><PropertiesPage /></ProtectedRoute>
            </Route>
            <Route exact path="/agents">
              <ProtectedRoute><AgentsPage /></ProtectedRoute>
            </Route>
            <Route exact path="/teams">
              <ProtectedRoute><TeamsPage /></ProtectedRoute>
            </Route>
            <Route exact path="/profiles">
              <ProtectedRoute><ProfilesPage /></ProtectedRoute>
            </Route>
            <Route exact path="/calls">
              <ProtectedRoute><CallsPage /></ProtectedRoute>
            </Route>
            <Route exact path="/call-report">
              <ProtectedRoute><CallReportPage /></ProtectedRoute>
            </Route>
            <Route exact path="/ai-agents">
              <ProtectedRoute><AIAgentsPage /></ProtectedRoute>
            </Route>
            <Route exact path="/phone-numbers">
              <ProtectedRoute><PhoneNumbersPage /></ProtectedRoute>
            </Route>
            <Route exact path="/voices">
              <ProtectedRoute><VoicesPage /></ProtectedRoute>
            </Route>
            <Route path="/content">
              <ProtectedRoute><ContentPage /></ProtectedRoute>
            </Route>
            <Route exact path="/sign-in">
              <SignInPage />
            </Route>
          </Switch>
        </div>
      </div>
    </>
  );
}

function AppContent() {
  // Log version once on app load
  useEffect(() => {
    const version = import.meta.env.VITE_VERSION;
    console.log(`Version: ${version}`);
    logInfo(`App loaded - Version: ${version}`);
  }, []);

  return (
    <IonApp>
      <IonReactRouter>
        <AppLayout />
        {/* Global Error Modal - catches unhandled API errors */}
        <GlobalErrorModal />
      </IonReactRouter>
    </IonApp>
  );
}

const App: React.FC = () => {
  return (
    <ToastProvider>
      <RefreshProvider>
        <InitAuth />
        <AppContent />
      </RefreshProvider>
    </ToastProvider>
  );
};

export default App;
